import React from 'react';
import { Box, Theme, Tooltip, useTheme } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridActionsCellItem,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  GridValueFormatterParams,
  GridEditInputCell,
  GridValueGetterParams,
  GridValueSetterParams,
  ValueOptions,
  GridComparatorFn,
  GridEditSingleSelectCellProps,
  useGridApiContext,
  GridEditSingleSelectCell,
  GridRenderEditCellParams
} from '@mui/x-data-grid';
import CancelIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import moment from 'moment';
import {
  ExternalCompany,
  MetricRecord,
  ReportingPeriod,
  File,
  EmissionFactor
} from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import {
  CalculatedMetricRecordClientData,
  MetricRecordFilters,
  UpdateMetricRecordClientData
} from '@esg/syntiro-api-core';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as FileIcon } from '../../../assets/icons/Attach.svg';
import { ReactComponent as FolderIcon } from '../../../assets/icons/Folder.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import {
  metric_record_label,
  updateMetricRecord,
  softDeleteMetricRecord,
  MetricRecordEntitiesMap,
  MetricRecordReportingEntity,
  MetricRecordDataLevel,
  MetricRecordDataLevelCollection,
  validQuantitativeMetricRecordValue,
  validQuantitativeMetricRecordProportion,
  getMetricRecords
} from '../../../graphql/services/metric_record.service';
import { UserContext } from '../../../context/UserContext';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { ViewAccess, getUserActivityOwners, getViewAccess } from '../../../util/user_access';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';
import QuantitativeMetricRecordGridToolbar from './QuantitativeMetricRecordGridToolbar';
import { reportingPeriodFilterByGroup } from '../../../lib/metric_capture/reporting_period';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { getFilesForMetricRecords } from '../../../lib/metric_capture/file';
import UploadSupportingDocsModal from '../supporting_docs/UploadSupportingDocsModal';
import ManageSupportingDocsModal from '../supporting_docs/ManageSupportingDocsModal';
import EditAverageRecordModal from './EditAverageRecordModal';
import { ActivityOwner } from '../../shared/input/select/ActivityOwnerSelect';
import { values } from '../../../util/general_utils';
import { MetricExtended } from '../../../lib/metric_capture/metric';
import { getEmissionFactorForMetricRecordParameters } from '../../../lib/app/emission_factor';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import {
  getCountryIdForMetricRecord,
  mapQuantitativeMetricRecordInputForRequest
} from '../../../graphql/utilities/metric_record.util';

const SOURCE = 'QuantitativeMetricRecordGrid.tsx';

export interface QuantitativeMetricRecordGridRow {
  id: string;
  reporting_period: ReportingPeriod;
  reporting_entity: MetricRecordReportingEntity;
  value: string;
  unit: string;
  proportion: number;
  note: string | null;
  external_company: ExternalCompany | null;
  docs: boolean;
  emission_factor_id?: string;
  is_averaged: boolean;
}

interface MetricRecordGridQuantitativeProps {
  capture_parameters: MetricRecordCaptureParameters;
  entities_map: MetricRecordEntitiesMap;
}

/**
 * Custom selector for editing the activity owner of a Metric Record to also manipulate the proportion .
 * @param {GridEditSingleSelectCellProps} props Predefined attributes of an MUI Datagrid Edit Cell element.
 * @returns {JSX.Element}
 */
const CustomActivityOwnerEditComponent = (props: GridEditSingleSelectCellProps): JSX.Element => {
  const api_ref: React.MutableRefObject<GridApiCommunity> = useGridApiContext();
  const handleValueChange = async (): Promise<void> => {
    await api_ref.current.setEditCellValue({
      id: props.id,
      field: 'proportion',
      value: 100
    });
  };
  return <GridEditSingleSelectCell onValueChange={handleValueChange} {...props} />;
};

/**
 * Datagrid handling full CRUD functionality for quantitative Metric Records.
 * @param {MetricRecordCaptureParameters} capture_parameters Object of parameter fields to use for Metric Record creation and editing.
 * @param {MetricRecordEntitiesMap} entities_map List of options user may set for Metric Record data.
 * @returns {JSX.Element}
 */
const QuantitativeMetricRecordGrid = ({
  capture_parameters,
  entities_map
}: MetricRecordGridQuantitativeProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  if (!group || !company) return null;

  const [gridLoading, setGridLoading] = React.useState<boolean>(true);
  const [metricRecordRows, setMetricRecordRows] = React.useState<
    Array<QuantitativeMetricRecordGridRow>
  >([]);
  const [actionRow, setActionRow] = React.useState<QuantitativeMetricRecordGridRow | null>(null);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [supportingDocs, setSupportingDocs] = React.useState<Array<File>>([]);
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
  const [showUploadDocsModal, setShowUploadDocsModal] = React.useState<boolean>(false);
  const [showManageDocsModal, setShowManageDocsModal] = React.useState<boolean>(false);
  const [showEditAverageModal, setShowEditAverageModal] = React.useState(false);
  const theme: Theme = useTheme();

  const view_access: ViewAccess =
    user && group && company
      ? getViewAccess(user, group.id, company.id, 'sustainability.metric_collection')
      : null;
  const view_only: boolean = view_access === 'read';

  const data_level_lookups: Record<
    MetricRecordDataLevel,
    { label: string; entities_index: MetricRecordDataLevelCollection }
  > = {
    national: { label: 'Country', entities_index: 'countries' },
    regional: { label: 'Region', entities_index: 'regions' },
    divisional: { label: 'Division', entities_index: 'divisions' },
    subdivisional: { label: 'Subdivision', entities_index: 'subdivisions' },
    site: { label: 'Site', entities_index: 'sites' }
  };

  const reporting_periods = values<ReportingPeriod>(entities_map.reporting_periods);
  const reporting_periods_for_group: Array<ReportingPeriod> = reportingPeriodFilterByGroup(
    capture_parameters.reporting_period_group?.id ?? '',
    reporting_periods
  );
  const unlocked_reporting_periods: Array<ReportingPeriod> = reporting_periods_for_group.filter(
    (reporting_period: ReportingPeriod) => !reporting_period.locked
  );
  const external_companies = values<ExternalCompany>(entities_map.external_companies);
  const emission_factors = values<EmissionFactor>(entities_map.emission_factors);

  const calculated_metrics = values<MetricExtended>(entities_map.metrics).filter(
    (metric: MetricExtended) =>
      metric.is_calculated &&
      metric.primary_metric_calculated &&
      metric.primary_metric_calculated.id === capture_parameters.metric?.id
  );

  const current_level_options: Array<MetricRecordReportingEntity> = capture_parameters.data_level
    ? values<MetricRecordReportingEntity>(
        entities_map[data_level_lookups[capture_parameters.data_level].entities_index]
      )
    : [];

  const averaged_value_warning =
    'This metric record was part of an averaged calculation across a Reporting Period Group. The changes will not be applied to any other associated metric records. Are you sure you want to proceed?';

  const activity_owner_options: Array<ActivityOwner> =
    user && company && group
      ? getUserActivityOwners(user, group.id, company, external_companies)
      : [];

  // From the given reporting data level, return the full relevant reporting entity object of the metric record based on field ids.
  const getEntityForMetricRecord = (
    metric_record: MetricRecord,
    metric_record_data_level: MetricRecordDataLevel
  ): MetricRecordReportingEntity | undefined => {
    let metric_record_reporting_entity: { id: string } | null = null;
    const entities_collection: MetricRecordDataLevelCollection =
      data_level_lookups[metric_record_data_level].entities_index;
    switch (metric_record_data_level) {
      case 'national':
        metric_record_reporting_entity = metric_record.country;
        break;
      case 'regional':
        metric_record_reporting_entity = metric_record.region;
        break;
      case 'divisional':
        metric_record_reporting_entity = metric_record.division;
        break;
      case 'subdivisional':
        metric_record_reporting_entity = metric_record.subdivision;
        break;
      case 'site':
        metric_record_reporting_entity = metric_record.site;
        break;
      default:
        break;
    }
    return metric_record_reporting_entity
      ? entities_map[entities_collection][metric_record_reporting_entity.id]
      : undefined;
  };

  const reportingPeriodComparator: GridComparatorFn<string> = (a: string, b: string) => {
    // Match string IDs to reporting periods to sort on end dates.
    const reporting_period_a: ReportingPeriod | undefined = entities_map.reporting_periods[a];
    const reporting_period_b: ReportingPeriod | undefined = entities_map.reporting_periods[b];
    return !!reporting_period_a && reporting_period_b
      ? moment(reporting_period_a.end).unix() - moment(reporting_period_b.end).unix()
      : 0;
  };

  // Handler functions
  const handleEditClick = (row: QuantitativeMetricRecordGridRow): void => {
    setActionRow(row);
    if (row.is_averaged) {
      setShowEditAverageModal(true);
    } else {
      setRowModesModel({ ...rowModesModel, [row.id]: { mode: GridRowModes.Edit } });
    }
  };

  const handleConfirmClick = (id: GridRowId): void => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId): void => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
  };

  const handleDeleteClick = (metric_record: QuantitativeMetricRecordGridRow): void => {
    setActionRow(metric_record);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
  };

  const handleCloseEditAverageModal = (): void => {
    setShowEditAverageModal(false);
  };

  const handleOpenUploadDocsModal = (action_row: QuantitativeMetricRecordGridRow): void => {
    setActionRow(action_row);
    setShowUploadDocsModal(true);
  };

  const handleOpenManageDocsModal = (action_row: QuantitativeMetricRecordGridRow): void => {
    setActionRow(action_row);
    setShowManageDocsModal(true);
  };

  const handleCloseUploadDocsModal = (): void => {
    setShowUploadDocsModal(false);
  };

  const handleCloseManageDocsModal = (): void => {
    setShowManageDocsModal(false);
  };

  const handleAddSupportingDoc = (row_id: string, supporting_doc: File): void => {
    setSupportingDocs([...supportingDocs, supporting_doc]);
    const new_rows: Array<QuantitativeMetricRecordGridRow> = metricRecordRows.map(
      (row: QuantitativeMetricRecordGridRow) => (row_id === row.id ? { ...row, docs: true } : row)
    );
    setMetricRecordRows(new_rows);
  };

  const handleRemoveSupportingDoc = (row_id: string, delete_doc_id: string): void => {
    const metric_record_docs: Array<File> = supportingDocs.filter(
      (doc: File) =>
        doc.metric_record && doc.metric_record.id === row_id && doc.id !== delete_doc_id
    );
    if (metric_record_docs.length === 0) {
      const new_rows: Array<QuantitativeMetricRecordGridRow> = metricRecordRows.map(
        (row: QuantitativeMetricRecordGridRow) =>
          row_id === row.id ? { ...row, docs: false } : row
      );
      setMetricRecordRows(new_rows);
    }
    setSupportingDocs(supportingDocs.filter((doc) => doc.id !== delete_doc_id));
  };

  const validateQuantitativeMetricRecord = (
    metric_record: QuantitativeMetricRecordGridRow
  ): boolean => {
    return Boolean(
      metric_record.reporting_period &&
      metric_record.reporting_entity &&
      validQuantitativeMetricRecordValue(metric_record.value) &&
      validQuantitativeMetricRecordProportion(
        metric_record.proportion,
        metric_record.external_company
      )
    );
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    try {
      setGridLoading(true);
      if (!group || !company || !capture_parameters.metric || !capture_parameters.data_level) {
        throw new Error('Cannot fetch Metric Records without required parameters');
      }
      // Prepare filters for metric records query.
      const reporting_period_ids: Array<string> = reporting_periods_for_group.map(
        (reporting_period: ReportingPeriod) => reporting_period.id
      );
      const metric_record_filters: MetricRecordFilters = {
        reporting_period_ids: reporting_period_ids,
        metric_group_id: capture_parameters.metric.metric_group.id,
        metric_id: capture_parameters.metric.id,
        data_level: capture_parameters.data_level
      };
      const metric_records: Array<MetricRecord> = await getMetricRecords(
        { group_id: group.id, company_id: company.id },
        metric_record_filters
      );
      const supporting_docs: Array<File> = await getFilesForMetricRecords(group.id, company.id);
      const metric_record_rows: Array<QuantitativeMetricRecordGridRow> = [];
      metric_records.forEach((metric_record: MetricRecord) => {
        const reporting_period: ReportingPeriod | undefined =
          entities_map.reporting_periods[metric_record.reporting_period?.id ?? ''];
        const external_company: ExternalCompany | null = metric_record.external_company
          ? entities_map.external_companies[metric_record.external_company.id]
          : null;
        const reporting_entity: MetricRecordReportingEntity | undefined =
          capture_parameters.data_level
            ? getEntityForMetricRecord(metric_record, capture_parameters.data_level)
            : undefined;
        if (
          !!reporting_period &&
          !!reporting_entity &&
          !(user?.type === 'external' && !external_company) &&
          external_company !== undefined
        ) {
          metric_record_rows.push({
            id: metric_record.id,
            reporting_period: reporting_period,
            reporting_entity: reporting_entity,
            value: metric_record.value.toString(),
            unit: capture_parameters.metric?.unit ?? '',
            proportion:
              metric_record.proportion !== null && metric_record.proportion !== undefined
                ? metric_record.proportion * 100
                : 100,
            note: metric_record.note ?? '',
            docs:
              supporting_docs.filter((file: File) => {
                return file.metric_record && file.metric_record.id === metric_record.id;
              }).length > 0,
            external_company: external_company,
            is_averaged: metric_record.is_averaged ?? false,
            emission_factor_id: metric_record.emission_factor?.id ?? ''
          });
        }
      });
      setSupportingDocs(supporting_docs);
      setMetricRecordRows(metric_record_rows);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: QuantitativeMetricRecordGrid failed on an unknown error while calling fetchRows.',
          {
            group: group,
            company: company,
            capture_parameters: capture_parameters
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch metric records. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  };

  const updateRow = async (
    old_metric_record: QuantitativeMetricRecordGridRow,
    new_metric_record: QuantitativeMetricRecordGridRow
  ): Promise<QuantitativeMetricRecordGridRow | undefined> => {
    try {
      setGridLoading(true);
      // If no inputs were changed, return the old row as is.
      if (
        new_metric_record.reporting_period.id === old_metric_record.reporting_period.id &&
        new_metric_record.reporting_entity.id === old_metric_record.reporting_entity.id &&
        new_metric_record.value === old_metric_record.value &&
        new_metric_record.proportion === old_metric_record.proportion &&
        new_metric_record.note === old_metric_record.note &&
        new_metric_record.external_company === old_metric_record.external_company
      ) {
        return old_metric_record;
      }
      // Validate the updated metric record data.
      const valid_input: boolean = validateQuantitativeMetricRecord(new_metric_record);
      if (!valid_input) {
        setFeedbackData({
          message: `Invalid inputs.`,
          state: true,
          type: 'warning'
        });
        return old_metric_record;
      }
      if (
        capture_parameters.metric &&
        capture_parameters.data_level &&
        new_metric_record.reporting_period &&
        new_metric_record.reporting_entity &&
        new_metric_record.value !== undefined &&
        group &&
        company
      ) {
        // Transform metric record row data into API required request data.
        if (new_metric_record.proportion === null || new_metric_record.proportion === undefined)
          new_metric_record.proportion = 100;
        const update_metric_record_client_data: Partial<UpdateMetricRecordClientData> =
          mapQuantitativeMetricRecordInputForRequest({
            metric: capture_parameters.metric,
            data_level: capture_parameters.data_level,
            value: Number(new_metric_record.value),
            reporting_period: new_metric_record.reporting_period,
            reporting_entity: new_metric_record.reporting_entity,
            emission_factors: emission_factors,
            note: new_metric_record.note,
            proportion: new_metric_record.proportion,
            external_company: new_metric_record.external_company,
            source: 'manual'
          });
        // Check for existing calculated metric records to update
        const calculated_metric_records_data: Array<CalculatedMetricRecordClientData> = [];
        if (calculated_metrics.length) {
          const calculated_metric_record_filters: MetricRecordFilters = {
            primary_metric_record_calculated_id: new_metric_record.id
          };
          const calculated_metric_records: Array<MetricRecord> = await getMetricRecords(
            { group_id: group.id, company_id: company.id },
            calculated_metric_record_filters
          );
          calculated_metric_records.forEach((calculated_metric_record: MetricRecord) => {
            const calculated_metric: MetricExtended | undefined =
              entities_map.metrics[calculated_metric_record.metric.id];
            if (calculated_metric) {
              const emission_factor: EmissionFactor | null =
                getEmissionFactorForMetricRecordParameters(
                  values<EmissionFactor>(entities_map.emission_factors),
                  calculated_metric,
                  new_metric_record.reporting_period,
                  getCountryIdForMetricRecord(
                    new_metric_record.reporting_entity,
                    capture_parameters.data_level
                  )
                );
              calculated_metric_records_data.push({
                calculated_metric_record_id: calculated_metric_record.id,
                calculated_metric: { id: calculated_metric.id },
                calculated_metric_group: { id: calculated_metric.metric_group.id },
                emission_factor: emission_factor ? { id: emission_factor.id } : undefined,
                scope: new_metric_record.external_company
                  ? { id: new_metric_record.external_company.scope.id }
                  : calculated_metric.scope
                    ? { id: calculated_metric.scope.id }
                    : null
              });
            }
          });
        }
        // Update metric record data in database and memory.
        await updateMetricRecord(
          { group_id: group.id, company_id: company.id },
          {
            ...update_metric_record_client_data,
            id: new_metric_record.id,
            calculated_metric_records: calculated_metric_records_data
          },
          {
            return_data: true,
            create_audit_log: true
          }
        );
        setMetricRecordRows([
          ...metricRecordRows.map((metric_record: QuantitativeMetricRecordGridRow) => {
            return metric_record.id === new_metric_record.id ? new_metric_record : metric_record;
          })
        ]);
      }
      return new_metric_record;
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            group: group,
            company: company,
            old_metric_record: old_metric_record,
            new_metric_record: new_metric_record
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to update metric record', tracking_id),
        state: true,
        type: 'error'
      });
      return old_metric_record;
    } finally {
      setGridLoading(false);
    }
  };

  const deleteRow = async (metric_record: QuantitativeMetricRecordGridRow): Promise<void> => {
    try {
      // Check for existing calculated metric records to delete
      const calculated_metric_records_data: Array<CalculatedMetricRecordClientData> = [];
      if (calculated_metrics.length) {
        const calculated_metric_record_filters: MetricRecordFilters = {
          primary_metric_record_calculated_id: metric_record.id
        };
        const calculated_metric_records: Array<MetricRecord> = await getMetricRecords(
          { group_id: group.id, company_id: company.id },
          calculated_metric_record_filters
        );
        calculated_metric_records.forEach((calculated_metric_record: MetricRecord) => {
          const calculated_metric: MetricExtended | undefined =
            entities_map.metrics[calculated_metric_record.metric.id];
          if (calculated_metric) {
            calculated_metric_records_data.push({
              calculated_metric_record_id: calculated_metric_record.id,
              calculated_metric: { id: calculated_metric.id },
              calculated_metric_group: { id: calculated_metric.metric_group.id }
            });
          }
        });
      }

      await softDeleteMetricRecord(
        { group_id: group.id, company_id: company.id },
        {
          id: metric_record.id,
          calculated_metric_records: calculated_metric_records_data
        }
      );
      setMetricRecordRows(
        metricRecordRows.filter(
          (row: QuantitativeMetricRecordGridRow) => row.id !== metric_record.id
        )
      );
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            group_id: group?.id,
            company_id: company?.id,
            metric_record_id: metric_record.id,
            metric_record_value: metric_record
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to delete metric record', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  const refreshMetricRecordsGrid = (): void => {
    try {
      setRowModesModel({});
      fetchRows();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'refreshMetricRecordsGrid'),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to refresh metric records', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  React.useEffect(() => {
    try {
      setRowModesModel({});
      fetchRows();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: QuantitativeMetricRecordGrid failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch metric records. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [capture_parameters.metric]);

  const columns: GridColDef[] = [
    {
      field: 'reporting_period',
      headerName: 'Month',
      headerAlign: 'left',
      type: 'singleSelect',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span>{params.row.reporting_period.name}</span>
            {params.row.reporting_period.locked && (
              <LockIcon sx={{ ml: 1, color: 'primary.main', fontSize: '1rem' }} />
            )}
          </Box>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        return params.value ? params.value.id : '';
      },
      valueSetter: (params: GridValueSetterParams) => {
        if (params.value) {
          const reporting_period: ReportingPeriod | undefined =
            entities_map.reporting_periods[params.value];
          return { ...params.row, reporting_period: reporting_period };
        }
        return params.row;
      },
      valueOptions: () => {
        return unlocked_reporting_periods;
      },
      getOptionValue: (value: ValueOptions) =>
        typeof value === 'object' && 'id' in value ? value.id : '',
      getOptionLabel: (value: ValueOptions) =>
        typeof value === 'object' && 'name' in value ? value.name : '',
      sortComparator: reportingPeriodComparator,
      flex: 0.8,
      editable: true
    },
    {
      field: 'reporting_entity',
      headerName: capture_parameters.data_level
        ? data_level_lookups[capture_parameters.data_level].label
        : 'Entity',
      headerAlign: 'left',
      type: 'singleSelect',
      renderCell: (params: GridRenderCellParams) => {
        return params.row.reporting_entity.name;
      },
      valueGetter: (params: GridValueGetterParams) => (params.value ? params.value.id : ''),
      valueSetter: (params: GridValueSetterParams) => {
        if (params.value) {
          const reporting_entity: MetricRecordReportingEntity | undefined =
            current_level_options.find(
              (reporting_entity: MetricRecordReportingEntity) =>
                reporting_entity.id === params.value
            );
          return { ...params.row, reporting_entity: reporting_entity };
        }
        return params.row;
      },
      valueOptions: () => current_level_options,
      getOptionValue: (value: ValueOptions) =>
        typeof value === 'object' && 'id' in value ? value.id : '',
      getOptionLabel: (value: ValueOptions) =>
        typeof value === 'object' && 'name' in value ? value.name : '',
      flex: 1.2,
      editable: true
    },
    {
      field: 'value',
      headerName: 'Value',
      headerAlign: 'left',
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          value={Number(params.value)
            .toFixed(10)
            .replace(/\.?0+$/, '')}
          inputProps={{
            min: 0
          }}
        />
      ),
      type: 'number',
      align: 'left',
      flex: 0.75,
      editable: true,
      renderCell: (params: GridRenderCellParams) => {
        return parseFloat(Number(params.value).toFixed(10));
      },
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        if (params.value == null) {
          return 0;
        }
        return parseFloat(Number(params.value).toFixed(10));
      }
    },
    {
      field: 'unit',
      headerName: 'Unit',
      align: 'left',
      headerAlign: 'left',
      flex: 0.5,
      editable: false
    },
    {
      field: 'proportion',
      headerName: 'Client Allocation (%)',
      headerAlign: 'left',
      type: 'number',
      align: 'left',
      flex: 1,
      editable: view_access !== 'admin' && user?.type === 'internal' ? false : true,
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            max: 100,
            min: 0
          }}
        />
      ),
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        if (params.value == null) {
          return 100;
        }
        return parseFloat(Number(params.value).toFixed(2));
      }
    },
    {
      field: 'note',
      headerName: 'Note',
      headerAlign: 'left',
      renderEditCell: (params) => <GridEditInputCell {...params} />,
      type: 'string',
      align: 'left',
      flex: 1.2,
      editable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        if (params.value == null) {
          return '';
        }
        return params.value;
      }
    },
    {
      field: 'external_company',
      headerName: 'Activity Owner',
      headerAlign: 'left',
      type: 'singleSelect',
      renderCell: (params: GridRenderCellParams) => {
        return params.row.external_company ? params.row.external_company.name : company?.name;
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <CustomActivityOwnerEditComponent variant="outlined" {...params} />
      ),
      valueGetter: (params: GridValueGetterParams) =>
        params.value === null ? (company?.id ?? '') : params.value.id,
      valueSetter: (params: GridValueSetterParams) => {
        if (params.value) {
          const external_company: ExternalCompany | undefined =
            entities_map.external_companies[params.value];
          return { ...params.row, external_company: external_company ?? null };
        }
        return params.row;
      },
      valueOptions: () => activity_owner_options,
      getOptionValue: (value: ValueOptions) =>
        typeof value === 'object' && 'id' in value ? value.id : '',
      getOptionLabel: (value: ValueOptions) =>
        typeof value === 'object' && 'name' in value ? value.name : '',
      flex: 1,
      editable: true
    }
  ];
  if (!view_only) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: '',
      align: 'right',
      flex: 1,
      getActions: ({ row }) => {
        const edit_row: boolean = rowModesModel[row.id]?.mode === GridRowModes.Edit;
        const locked_reporting_period: boolean = row.reporting_period.locked;
        if (edit_row) {
          return [
            <>
              <Tooltip title="Save Changes" key={1}>
                <span>
                  <GridActionsCellItem
                    id="save-record-changes"
                    icon={<CheckIcon sx={{ fontSize: '1.5rem' }} />}
                    label="Confirm"
                    onClick={() => {
                      handleConfirmClick(row.id);
                    }}
                    sx={{
                      color: '#053d5d'
                    }}
                  />
                </span>
              </Tooltip>
            </>,
            <>
              <Tooltip title="Discard Changes" key={2}>
                <span>
                  <GridActionsCellItem
                    icon={<CancelIcon />}
                    label="Cancel"
                    className="textPrimary"
                    color="inherit"
                    onClick={() => {
                      handleCancelClick(row.id);
                    }}
                  />
                </span>
              </Tooltip>
            </>
          ];
        }
        return locked_reporting_period
          ? []
          : [
              <>
                {row.docs && (
                  <>
                    <Tooltip title="Manage Supporting Documents" key={1}>
                      <span>
                        <GridActionsCellItem
                          icon={
                            <FolderIcon
                              height={20}
                              stroke={theme.palette.primary.main}
                              fill={theme.palette.primary.main}
                              strokeWidth={2}
                            />
                          }
                          label="Uploaded Documents"
                          onClick={() => handleOpenManageDocsModal(row)}
                          sx={{
                            color: '#053d5d'
                          }}
                          disabled={edit_row || view_only}
                        />
                      </span>
                    </Tooltip>
                  </>
                )}
              </>,
              <>
                <Tooltip title="Upload Supporting Document" key={2}>
                  <span>
                    <GridActionsCellItem
                      id="upload-supporting-docs"
                      icon={
                        <FileIcon
                          height={20}
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          strokeWidth={2}
                        />
                      }
                      label="Supporting Documents"
                      onClick={() => {
                        handleOpenUploadDocsModal(row);
                      }}
                      sx={{
                        color: '#053d5d'
                      }}
                      disabled={edit_row || view_only}
                    />
                  </span>
                </Tooltip>
              </>,
              <>
                <Tooltip title="Edit Record" key={3}>
                  <span>
                    <GridActionsCellItem
                      id="edit-metric-record"
                      disabled={view_only}
                      icon={
                        <EditIcon
                          width={20}
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          strokeWidth={2}
                        />
                      }
                      label="Edit"
                      onClick={() => {
                        handleEditClick(row);
                      }}
                      sx={{
                        color: 'primary.main'
                      }}
                    />
                  </span>
                </Tooltip>
              </>,
              <>
                <Tooltip title="Delete Record" key={4}>
                  <span>
                    <GridActionsCellItem
                      id="delete-metric-record"
                      disabled={view_only}
                      icon={
                        <DeleteIcon
                          width={20}
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          strokeWidth={2}
                        />
                      }
                      label="Delete"
                      onClick={() => {
                        handleDeleteClick(row);
                      }}
                      sx={{
                        color: 'primary.main'
                      }}
                    />
                  </span>
                </Tooltip>
              </>
            ];
      }
    });
  }

  return (
    <>
      <EditAverageRecordModal
        open={showEditAverageModal}
        handleCloseEditModal={handleCloseEditAverageModal}
        action_row={actionRow!}
        row_modes_model={rowModesModel}
        setRowModesModel={setRowModesModel}
        message={averaged_value_warning}
      />
      <UploadSupportingDocsModal
        open={showUploadDocsModal}
        handleCloseDocsModal={handleCloseUploadDocsModal}
        metric_record={actionRow}
        metric_record_title={`${capture_parameters?.metric?.name}: ${actionRow?.value} ${actionRow?.unit}`}
        handleAddSupportingDoc={handleAddSupportingDoc}
      />
      <ManageSupportingDocsModal
        open={showManageDocsModal}
        handleCloseDocsModal={handleCloseManageDocsModal}
        handleRemoveSupportingDoc={handleRemoveSupportingDoc}
        view_only={view_only}
        metric_record={actionRow}
        metric_record_title={`${capture_parameters?.metric?.name}: ${actionRow?.value} ${actionRow?.unit}`}
        supporting_docs={supportingDocs.filter((supporting_doc: File) => {
          return supporting_doc.metric_record && supporting_doc.metric_record.id === actionRow?.id;
        })}
      />
      <DeleteConfirmModal
        allow_delete={true}
        allow_archive={false}
        alternate_message_flag={actionRow?.is_averaged}
        alternate_message={averaged_value_warning}
        open={showDeleteModal}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={
          capture_parameters.metric &&
          actionRow &&
          `${capture_parameters.metric.name}: ${actionRow.value}`
        }
        delete_warning="Metric Record data will be deleted and not included in Reports"
        handleDelete={actionRow && (() => deleteRow(actionRow))}
      />
      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'reporting_period', sort: 'desc' }]
          },
          columns: {
            columnVisibilityModel: {
              proportion: user?.type === 'external' || activity_owner_options.length > 1,
              external_company: activity_owner_options.length > 1
            }
          }
        }}
        hideFooter={metricRecordRows.length > 1 ? false : true}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={metricRecordRows}
        columns={columns}
        disableRowSelectionOnClick
        editMode="row"
        rowModesModel={rowModesModel}
        processRowUpdate={(updatedRow, originalRow) => {
          try {
            return updateRow(originalRow, updatedRow);
          } catch (err: unknown) {
            const tracking_id: string = generateUuidV4();
            log(
              'error',
              new MetadataError(
                err instanceof Error
                  ? err.message
                  : 'Error: QuantitativeMetricRecordGrid failed on an unknown error while updating row.',
                {
                  originalRow: originalRow,
                  updatedRow: updatedRow
                },
                tracking_id
              )
            );
            setFeedbackData({
              message: `Unable to process row update. Tracking ID: ${tracking_id}`,
              state: true,
              type: 'error'
            });
          }
        }}
        onProcessRowUpdateError={(err: unknown) => {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err instanceof Error
                ? err.message
                : 'Error: QuantitativeMetricRecordGrid: DataGrid failed on an unknown error.',
              null,
              tracking_id
            )
          );
          setFeedbackData({
            message: `Unable to update row. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        }}
        slots={{
          toolbar: view_only ? null : QuantitativeMetricRecordGridToolbar,
          noRowsOverlay: () => <DataGridNoRows missing_label={metric_record_label} />
        }}
        slotProps={{
          toolbar: {
            entities_map: entities_map,
            capture_parameters: capture_parameters,
            metric_record_rows: metricRecordRows,
            handleUpdateMetricRecordRows: (new_rows: Array<QuantitativeMetricRecordGridRow>) =>
              setMetricRecordRows(new_rows),
            refreshMetricRecordsGrid: refreshMetricRecordsGrid
          }
        }}
        disableColumnSelector={true}
        sx={{ minWidth: 0, overflow: 'auto' }}
      />
    </>
  );
};

export default QuantitativeMetricRecordGrid;
